import React, { createContext, ReactNode, useCallback, useMemo, useState } from 'react';
import { DischargeContextType, DischargePatient, DischargePhase, DischargeDisposition } from '~/types/discharge';
import { buildAgencyFollowUp } from '~/components/discharge/patient-detail/buildAgencyFollowUp';

// Demo: hardcoded current user for the Discharge prototype. Matches the
// Coordinate prototype's DEMO_CURRENT_USER so "My Patients" lines up.
export const DEMO_CURRENT_USER = 'Sarah Johnson';

export const DischargeContext = createContext<DischargeContextType | undefined>(undefined);

// Demo: seeded 90-day timelines are expressed relative to "now" so a patient's
// history stays consistent whenever the demo is run, matching how `admitDate`
// is re-anchored at load time.
function daysAgoIso(days: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

// Seeded patients omit `recommendedDisposition` — it's derived from the
// initial `disposition` at load time so it stays fixed even when the user
// changes their selected disposition.
const INITIAL_PATIENTS: Omit<DischargePatient, 'recommendedDisposition'>[] = [
  {
    id: 'disch-01',
    dispositionScore: 73,
    currentLOS: 2,
    name: 'Margaret Sullivan',
    age: 74,
    dob: '03/12/1951',
    mrn: 'MRN104821',
    facilityName: 'Metro General Hospital',
    floor: 4, room: 412,
    disposition: 'home_health',
    urgency: 'urgent',
    phase: 'predict',
    insurance: 'Medicare',
    insuranceType: 'medicare',
    memberId: 'M1A-774-22891',
    admitDate: '4/24/2026',
    losTargetDays: 5,
    attendingPhysician: 'Dr. Evelyn Park',
    primaryDiagnosis: 'CHF exacerbation',
    primaryContact: { name: 'Robert Sullivan', relationship: 'Son', phone: '(212) 555-0142' },
    assignedTo: 'Sarah Johnson',
  },
  {
    id: 'disch-02',
    dispositionScore: 87,
    currentLOS: 4,
    name: 'Robert Chen',
    age: 68,
    dob: '07/29/1956',
    mrn: 'MRN221993',
    facilityName: 'City Medical Center',
    floor: 6, room: 614,
    disposition: 'home_health',
    urgency: 'routine',
    phase: 'place',
    insurance: 'Medicare Advantage',
    insuranceType: 'medicare_advantage',
    memberId: 'MA-332-10044',
    admitDate: '4/22/2026',
    losTargetDays: 7,
    attendingPhysician: 'Dr. James Okafor',
    primaryDiagnosis: 'Post-surgical wound',
    primaryContact: { name: 'Linda Chen', relationship: 'Wife', phone: '(212) 555-0291' },
    assignedTo: 'Michael Chen',
  },
  {
    id: 'disch-03',
    dispositionScore: 91,
    currentLOS: 6,
    name: 'Dorothy Williams',
    age: 81,
    dob: '11/05/1943',
    mrn: 'MRN305127',
    facilityName: 'Metro General Hospital',
    floor: 3, room: 318,
    disposition: 'home_health',
    urgency: 'routine',
    phase: 'predict',
    insurance: 'Medicare',
    insuranceType: 'medicare',
    memberId: 'M1A-901-33210',
    admitDate: '4/20/2026',
    losTargetDays: 8,
    attendingPhysician: 'Dr. Marcus Webb',
    primaryDiagnosis: 'Hip fracture repair',
    primaryContact: { name: 'Margaret Hayes', relationship: 'Daughter', phone: '(212) 555-0330' },
    assignedTo: 'Sarah Johnson',
    gender: 'Female',
    ninetyDaySummary: {
      trend: 'stable',
      trendDescription: 'Utilization has been episodic rather than escalating — no sign of a deteriorating trajectory, but the frequency of acute-care contact suggests some fragility in home support and self-management that warrants continued monitoring.',
      recentAdmissions: 2,
      erVisits: 3,
      totalInpatientDays: 13,
      currentLOS: 6,
      narrativeSummary: `Ms. Williams is an 81-year-old female admitted 6 days ago following a hip fracture that required operative fixation[[1]]. Her post-operative course has been unremarkable to date — she has tolerated early mobility and physical therapy without acute complications, and the surgical site is healing as expected[[2]]. Pain is controlled on an oral regimen, and she is participating in twice-daily PT/OT focused on transfer training and ambulation with an assistive device[[3]].

Over the past 90 days she has had 2 inpatient admissions including the current stay, 3 ED visits, and 13 total inpatient days[[4]]. The pattern is episodic rather than escalating: there is no deteriorating trajectory, but the frequency of acute-care contact — and the fall-related nature of this injury — points to some fragility in her home support and self-management.

Clinically she has been broadly stable across the window. Her comorbidities (hypertension, mild osteoarthritis, and age-related deconditioning) remain manageable on existing regimens, with no recent escalation in medication complexity and no new diagnoses[[5]]. Vitals and labs this admission have stayed within expected post-surgical ranges.

Caregiver engagement is confirmed with her daughter, who has been responsive and present throughout the admission and is expected to be involved in discharge planning and home support. No red flags were identified regarding caregiver capacity, though a home safety evaluation is recommended given the fall. Insurance authorization has been reviewed with no current barriers to post-acute placement[[6]].`,
      references: [
        { id: 1, source: 'Operative Report', sourceType: 'note', detail: 'Orthopedic Surgery · 5 days ago', snippet: 'Operative fixation of hip fracture; procedure tolerated well' },
        { id: 2, source: 'Nursing Wound Assessment', sourceType: 'assessment', detail: 'Nursing · current', snippet: 'Surgical site clean, dry and intact; healing as expected' },
        { id: 3, source: 'PT / OT Progress Notes', sourceType: 'note', detail: 'Rehabilitation Services · current', snippet: 'Twice-daily transfer training and ambulation with assistive device' },
        { id: 4, source: 'Encounter / Utilization History', sourceType: 'claims', detail: 'Utilization summary · last 90 days', snippet: '2 admissions, 3 ED visits, 13 inpatient days' },
        { id: 5, source: 'Problem List / Medication Review', sourceType: 'ehr', detail: 'Chart review · current', snippet: 'Hypertension, mild osteoarthritis, age-related deconditioning; no regimen escalation' },
        { id: 6, source: 'Authorization Review', sourceType: 'claims', detail: 'Case Management · current', snippet: 'Medicare; no barriers to post-acute placement' },
      ],
      keyFindings: [
        'Primary diagnosis: hip fracture repair, post-operative day 6, stable recovery trajectory',
        'Utilization: 2 inpatient admissions and 3 ED visits in the past 90 days (13 total inpatient days)',
        'Authorization: no current barriers to post-acute placement',
        'Caregiver: engagement confirmed with primary contact; home safety evaluation recommended',
        'Overall risk profile: moderate — clinically stable, but utilization pattern and fall history call for coordinated post-acute follow-up to prevent readmission',
      ],
      events: [
        { date: daysAgoIso(4), type: 'pt_eval', location: 'Metro General Hospital — Rehabilitation Services', description: 'PT/OT initiated; twice-daily transfer training and ambulation with assistive device' },
        { date: daysAgoIso(5), type: 'procedure', location: 'Metro General Hospital — Orthopedic Surgery', description: 'Operative fixation of hip fracture; procedure tolerated well' },
        { date: daysAgoIso(6), type: 'admission', location: 'Metro General Hospital — Emergency Department', description: 'Admitted via ED after a mechanical fall at home; hip fracture confirmed on imaging' },
        { date: daysAgoIso(22), type: 'er_visit', location: 'Metro General Hospital — ED', description: 'ED visit after a mechanical fall at home; no fracture, discharged same day' },
        { date: daysAgoIso(38), type: 'er_visit', location: 'Metro General Hospital — ED', description: 'ED visit for poorly controlled hip and knee pain; oral analgesia adjusted' },
        { date: daysAgoIso(45), type: 'discharge', location: 'Metro General Hospital', description: 'Discharged after 7-day stay for fall-related deconditioning; home health initiated', destination: 'Home + Home Health' },
        { date: daysAgoIso(52), type: 'admission', location: 'Metro General Hospital — Internal Medicine', description: 'Admitted after a mechanical fall at home; treated for dehydration and deconditioning' },
        { date: daysAgoIso(68), type: 'er_visit', location: 'Metro General Hospital — ED', description: 'ED visit for dizziness and a near-fall; evaluated and discharged same day' },
      ],
    },
  },
  {
    id: 'disch-04',
    dispositionScore: 89,
    currentLOS: 1,
    name: 'James Patterson',
    age: 59,
    dob: '02/14/1965',
    mrn: 'MRN418776',
    facilityName: 'St. Vincent Medical',
    floor: 5, room: 521,
    disposition: 'infusion_at_home',
    urgency: 'emergent',
    phase: 'refer',
    insurance: 'Commercial — Aetna PPO',
    insuranceType: 'commercial',
    memberId: 'C-AET-554-101',
    admitDate: '4/26/2026',
    losTargetDays: 4,
    attendingPhysician: 'Dr. Helena Ng',
    primaryDiagnosis: 'IV antibiotics required',
    primaryContact: { name: 'Diane Patterson', relationship: 'Wife', phone: '(212) 555-0419' },
    assignedTo: 'Sarah Johnson',
    selectedProviderName: 'Advanced Home Infusion',
    gender: 'Male',
  },
  {
    id: 'disch-05',
    dispositionScore: 55,
    currentLOS: 2,
    name: 'Linda Ortiz',
    age: 65,
    dob: '08/01/1960',
    mrn: 'MRN509144',
    facilityName: 'City Medical Center',
    floor: 7, room: 705,
    disposition: 'snf_at_home',
    urgency: 'routine',
    phase: 'predict',
    insurance: 'Medicaid',
    insuranceType: 'medicaid',
    memberId: 'MCD-201-44781',
    admitDate: '4/25/2026',
    losTargetDays: 6,
    attendingPhysician: 'Dr. Priya Shah',
    primaryDiagnosis: 'Pneumonia',
    primaryContact: { name: 'Carlos Ortiz', relationship: 'Son', phone: '(212) 555-0512' },
    assignedTo: null,
  },
  {
    id: 'disch-06',
    dispositionScore: 82,
    currentLOS: 3,
    name: 'Walter Brooks',
    age: 77,
    dob: '05/18/1948',
    mrn: 'MRN611002',
    facilityName: 'Metro General Hospital',
    floor: 4, room: 417,
    disposition: 'hospice',
    urgency: 'urgent',
    phase: 'predict',
    insurance: 'Medicare',
    insuranceType: 'medicare',
    memberId: 'M1A-555-77123',
    admitDate: '4/23/2026',
    losTargetDays: 3,
    attendingPhysician: 'Dr. Marcus Webb',
    primaryDiagnosis: 'End-stage COPD',
    primaryContact: { name: 'Helen Brooks', relationship: 'Daughter', phone: '(212) 555-0617' },
    assignedTo: 'Sarah Johnson',
  },
  {
    id: 'disch-07',
    dispositionScore: 78,
    currentLOS: 0,
    name: 'Aisha Robinson',
    age: 52,
    dob: '12/22/1973',
    mrn: 'MRN720488',
    facilityName: 'St. Vincent Medical',
    floor: 2, room: 213,
    disposition: 'infusion_at_home',
    urgency: 'routine',
    phase: 'predict',
    insurance: 'Commercial — UHC',
    insuranceType: 'commercial',
    memberId: 'C-UHC-882-220',
    admitDate: '4/27/2026',
    losTargetDays: 5,
    attendingPhysician: 'Dr. Helena Ng',
    primaryDiagnosis: 'Osteomyelitis',
    primaryContact: { name: 'Marcus Robinson', relationship: 'Brother', phone: '(212) 555-0744' },
    assignedTo: null,
  },
  {
    id: 'disch-08',
    dispositionScore: 83,
    currentLOS: 5,
    name: 'George Park',
    age: 70,
    dob: '06/10/1955',
    mrn: 'MRN830771',
    facilityName: 'City Medical Center',
    floor: 5, room: 509,
    disposition: 'home_health',
    urgency: 'routine',
    phase: 'refer',
    insurance: 'Medicare Advantage',
    insuranceType: 'medicare_advantage',
    memberId: 'MA-771-50018',
    admitDate: '4/21/2026',
    losTargetDays: 7,
    attendingPhysician: 'Dr. James Okafor',
    primaryDiagnosis: 'Stroke recovery',
    primaryContact: { name: 'Susan Park', relationship: 'Spouse', phone: '(212) 555-0822' },
    assignedTo: 'Michael Chen',
  },
  {
    id: 'disch-09',
    dispositionScore: 86,
    currentLOS: 4,
    name: 'Eleanor Davis',
    age: 84,
    dob: '01/04/1941',
    mrn: 'MRN911339',
    facilityName: 'Metro General Hospital',
    floor: 6, room: 618,
    disposition: 'palliative',
    urgency: 'routine',
    phase: 'monitor',
    insurance: 'Medicare',
    insuranceType: 'medicare',
    memberId: 'M1A-114-90021',
    admitDate: '4/22/2026',
    losTargetDays: 6,
    attendingPhysician: 'Dr. Evelyn Park',
    primaryDiagnosis: 'Advanced dementia',
    primaryContact: { name: 'Rachel Davis', relationship: 'Daughter', phone: '(212) 555-0911' },
    assignedTo: 'Sarah Johnson',
  },
  {
    id: 'disch-10',
    dispositionScore: 71,
    currentLOS: 0,
    name: 'Thomas Whitfield',
    age: 61,
    dob: '09/30/1964',
    mrn: 'MRN102877',
    facilityName: 'St. Vincent Medical',
    floor: 4, room: 408,
    disposition: 'home_health',
    urgency: 'routine',
    phase: 'predict',
    insurance: 'Commercial — BCBS',
    insuranceType: 'commercial',
    memberId: 'C-BCBS-401-55',
    admitDate: '4/27/2026',
    losTargetDays: 4,
    attendingPhysician: 'Dr. Helena Ng',
    primaryDiagnosis: 'Cardiac monitoring',
    primaryContact: { name: 'Karen Whitfield', relationship: 'Wife', phone: '(212) 555-1003' },
    assignedTo: null,
  },
  {
    id: 'disch-11',
    dispositionScore: 84,
    currentLOS: 3,
    name: 'Patricia Nguyen',
    age: 73,
    dob: '04/17/1952',
    mrn: 'MRN114225',
    facilityName: 'Metro General Hospital',
    floor: 3, room: 309,
    disposition: 'home_health',
    urgency: 'routine',
    phase: 'place',
    insurance: 'Medicare',
    insuranceType: 'medicare',
    memberId: 'M1A-220-66401',
    admitDate: '4/23/2026',
    losTargetDays: 6,
    attendingPhysician: 'Dr. Marcus Webb',
    primaryDiagnosis: 'Knee replacement recovery',
    primaryContact: { name: 'An Nguyen', relationship: 'Son', phone: '(212) 555-1129' },
    assignedTo: 'Sarah Johnson',
  },
  {
    id: 'disch-12',
    dispositionScore: 62,
    currentLOS: 2,
    name: 'Hector Alvarez',
    age: 56,
    dob: '10/08/1969',
    mrn: 'MRN127554',
    facilityName: 'City Medical Center',
    floor: 4, room: 422,
    disposition: 'snf_at_home',
    urgency: 'routine',
    phase: 'predict',
    insurance: 'Medicaid',
    insuranceType: 'medicaid',
    memberId: 'MCD-808-21155',
    admitDate: '4/25/2026',
    losTargetDays: 5,
    attendingPhysician: 'Dr. Priya Shah',
    primaryDiagnosis: 'Sepsis recovery',
    primaryContact: { name: 'Maria Alvarez', relationship: 'Sister', phone: '(212) 555-1234' },
    assignedTo: null,
  },
  // Carried over from the Predict demo as-is (same identifying/clinical details,
  // disposition + score from each patient's recommended program). Discharge-only
  // fields Predict doesn't track (floor/room, memberId, primaryContact) are demo
  // placeholders; admitDate is derived from currentLOS at load.
  {
    id: 'disch-13',
    dispositionScore: 85,
    currentLOS: 0.5,
    name: 'Angela Martinez',
    age: 56,
    dob: '03/15/1968',
    mrn: 'MRN001235',
    facilityName: 'Metro General Hospital',
    floor: 4, room: 423,
    disposition: 'home_health',
    urgency: 'routine',
    phase: 'predict',
    insurance: 'Blue Cross Blue Shield',
    insuranceType: 'commercial',
    memberId: 'BCB-774-51230',
    admitDate: '4/26/2026',
    losTargetDays: 3,
    attendingPhysician: 'Dr. Michael Rodriguez',
    primaryDiagnosis: 'Cellulitis requiring IV antibiotics',
    primaryContact: { name: 'Carlos Martinez', relationship: 'Son', phone: '(212) 555-0173' },
    programScores: { home_health: 85, infusion_at_home: 76, snf_at_home: 68 },
    decisionFactors: {
      home_health: [
        { id: 'disch-13-home_health-1', label: 'IV Therapy Requirements', importance: 90, value: 'PIV line for IV antibiotics', dataSource: 'Pharmacy Orders', timestamp: '2026-07-29T08:16:00', contribution: 'positive' },
        { id: 'disch-13-home_health-2', label: 'Wound Care', importance: 88, value: 'Requires daily wound assessment', dataSource: 'Nursing Assessment', timestamp: '2026-07-29T08:16:00', contribution: 'positive' },
        { id: 'disch-13-home_health-3', label: 'Caregiver Support', importance: 82, value: 'Daughter available for assistance', dataSource: 'Social Work Notes', timestamp: '2026-07-29T08:16:00', contribution: 'positive' },
      ],
      infusion_at_home: [
        { id: 'disch-13-infusion_at_home-1', label: 'IV Antibiotic Administration', importance: 88, value: 'Daily IV antibiotics via PIV line', dataSource: 'Pharmacy Orders', timestamp: '2026-07-29T08:16:00', contribution: 'positive' },
        { id: 'disch-13-infusion_at_home-2', label: 'Simple Protocol Appropriate', importance: 75, value: 'Straightforward once-daily antibiotic regimen', dataSource: 'Pharmacy Review', timestamp: '2026-07-29T08:16:00', contribution: 'positive' },
        { id: 'disch-13-infusion_at_home-3', label: 'Comprehensive Wound Care', importance: 70, value: 'Cellulitis requires daily wound assessment and monitoring', dataSource: 'Nursing Assessment', timestamp: '2026-07-29T08:16:00', contribution: 'negative' },
      ],
      snf_at_home: [
        { id: 'disch-13-snf_at_home-1', label: 'Skilled Nursing Capability', importance: 78, value: 'SNF at Home provides intensive daily nursing care', dataSource: 'Clinical Assessment', timestamp: '2026-07-29T08:16:00', contribution: 'positive' },
        { id: 'disch-13-snf_at_home-2', label: 'Rehabilitation Needs', importance: 75, value: 'No significant rehabilitation requirements', dataSource: 'PT Evaluation', timestamp: '2026-07-29T08:16:00', contribution: 'negative' },
        { id: 'disch-13-snf_at_home-3', label: 'Care Appropriateness', importance: 72, value: 'Uncomplicated cellulitis case', dataSource: 'Case Management', timestamp: '2026-07-29T08:16:00', contribution: 'negative' },
      ],
    },
    ninetyDaySummary: {
      trend: 'stable',
      trendDescription: 'Recurrent cellulitis episodes related to chronic venous insufficiency. Current episode responding well to IV antibiotics. Overall stable with good support at home.',
      recentAdmissions: 1,
      erVisits: 1,
      totalInpatientDays: 0.5,
      narrativeSummary: `Ms. Martinez is a 56-year-old female presenting with acute cellulitis of the right lower extremity requiring IV antibiotic therapy[[1]]. Over the past 90 days, she has had one previous ED visit for similar concerns[[2]].\n\nHer clinical course has been characterized by recurrent soft tissue infections, likely related to venous insufficiency and minor skin breakdown. The current episode represents a more severe presentation requiring IV antibiotics via PICC line for home infusion therapy.\n\nRecent imaging confirmed no abscess formation or deep tissue involvement[[3]]. Vascular studies showed chronic venous insufficiency but no acute DVT[[4]]. Her overall medical complexity is moderate, with well-controlled diabetes and hypertension as comorbidities[[5]].`,
      references: [
        { id: 1, source: 'H&P / Admission Note', sourceType: 'note', detail: 'Dr. S. Alvarez · on admission', snippet: 'Acute cellulitis, RLE; admitted for IV antibiotics' },
        { id: 2, source: 'Encounter / Utilization History', sourceType: 'claims', detail: 'Utilization summary · last 90 days', snippet: '1 prior ED visit for RLE cellulitis' },
        { id: 3, source: 'Soft Tissue Ultrasound', sourceType: 'imaging', detail: 'Radiology · 1 day ago', snippet: 'No drainable abscess or deep tissue involvement' },
        { id: 4, source: 'Venous Duplex Study', sourceType: 'imaging', detail: 'Vascular Lab · 2 days ago', snippet: 'Chronic venous insufficiency; no acute DVT' },
        { id: 5, source: 'Problem List', sourceType: 'ehr', detail: 'Chart data', snippet: 'Type 2 diabetes and hypertension, well-controlled' },
      ],
      keyFindings: [
        'Recurrent cellulitis related to chronic venous insufficiency',
        'PICC line placed for 14-day home IV antibiotic course',
        'Daughter trained and competent in PICC line care',
        'Diabetes and hypertension well-controlled',
        'Lives with daughter who provides excellent caregiver support',
      ],
      events: [
        { date: '2026-07-29T00:00:00', type: 'admission', location: 'Metro General Hospital - Emergency Department', description: 'Admitted from ED with cellulitis requiring IV antibiotics' },
        { date: '2026-07-29T00:00:00', type: 'procedure', location: 'Metro General Hospital', description: 'PICC line placement for home IV therapy' },
        { date: '2026-07-29T00:00:00', type: 'other', location: 'Metro General Hospital - Nursing Education', description: 'Daughter trained on PICC line care and medication administration' },
        { date: '2026-06-19T00:00:00', type: 'er_visit', location: 'Metro General Hospital - ED', description: 'ED visit for mild cellulitis, treated with oral antibiotics' },
      ],
    },
    assignedTo: 'Sarah Johnson',
  },
  {
    id: 'disch-14',
    dispositionScore: 85,
    currentLOS: 3,
    name: 'Jennifer Lee',
    age: 39,
    dob: '09/18/1985',
    mrn: 'MRN001238',
    facilityName: 'City Medical Center',
    floor: 6, room: 618,
    disposition: 'infusion_at_home',
    urgency: 'routine',
    phase: 'predict',
    insurance: 'Aetna',
    insuranceType: 'commercial',
    memberId: 'AET-233-88190',
    admitDate: '4/23/2026',
    losTargetDays: 5,
    attendingPhysician: 'Dr. Robert Kim',
    primaryDiagnosis: 'Cellulitis',
    primaryContact: { name: 'David Lee', relationship: 'Husband', phone: '(212) 555-0188' },
    programScores: { infusion_at_home: 85, home_health: 72 },
    decisionFactors: {
      infusion_at_home: [
        { id: 'disch-14-infusion_at_home-1', label: 'IV Antibiotic Therapy', importance: 90, value: 'Daily IV antibiotics needed', dataSource: 'Pharmacy Orders', timestamp: '2026-07-29T08:16:00', contribution: 'positive' },
        { id: 'disch-14-infusion_at_home-2', label: 'Clinical Response', importance: 85, value: 'Improving on treatment', dataSource: 'Physician Notes', timestamp: '2026-07-29T08:16:00', contribution: 'positive' },
        { id: 'disch-14-infusion_at_home-3', label: 'Clinical Simplicity', importance: 78, value: 'Uncomplicated cellulitis case', dataSource: 'ED Assessment', timestamp: '2026-07-29T08:16:00', contribution: 'positive' },
      ],
      home_health: [
        { id: 'disch-14-home_health-1', label: 'Skilled Nursing Capability', importance: 82, value: 'Wound care and IV management', dataSource: 'Nursing Assessment', timestamp: '2026-07-29T08:16:00', contribution: 'positive' },
        { id: 'disch-14-home_health-2', label: 'Caregiver Support', importance: 75, value: 'Lives independently, capable of self-care', dataSource: 'Social Work Notes', timestamp: '2026-07-29T08:16:00', contribution: 'positive' },
        { id: 'disch-14-home_health-3', label: 'Service Specialization', importance: 70, value: 'Infusion services specialize in IV therapy', dataSource: 'Care Coordination', timestamp: '2026-07-29T08:16:00', contribution: 'negative' },
      ],
    },
    ninetyDaySummary: {
      trend: 'improving',
      trendDescription: 'Rapid clinical improvement on IV antibiotic therapy. Cellulitis resolving with decreasing erythema and swelling. Afebrile x24 hours. Ready for home IV therapy completion.',
      recentAdmissions: 1,
      erVisits: 1,
      totalInpatientDays: 3,
      narrativeSummary: `Ms. Lee is a 39-year-old female presenting with severe cellulitis of the right lower extremity requiring IV antibiotic therapy[[1]]. Over the past 90 days, she has been generally healthy with no significant hospital encounters or medical issues prior to this acute infection[[2]].\n\nShe initially presented to the ED 3 days ago with a painful, red, swollen right leg that had progressively worsened over 48 hours. She was started on IV ceftriaxone after blood cultures were drawn[[3]]. The cellulitis has shown excellent clinical response with decreasing erythema, reduced swelling, and resolution of fever. She has remained afebrile for 24 hours[[4]] and her pain is well-controlled.\n\nA PICC line was placed for home IV antibiotic therapy completion[[5]]. She is being discharged with Infusion at Home services to complete a 7-day course of IV ceftriaxone. She lives independently and is capable of managing her care. Her overall prognosis is excellent with full recovery expected after completing the antibiotic course.`,
      references: [
        { id: 1, source: 'ED Provider Note', sourceType: 'note', detail: 'Dr. R. Kim, Emergency Medicine · 3 days ago', snippet: 'Severe RLE cellulitis; IV antibiotics initiated' },
        { id: 2, source: 'Encounter / Utilization History', sourceType: 'claims', detail: 'Utilization summary · last 90 days', snippet: 'No prior admissions or ED visits' },
        { id: 3, source: 'Blood Culture Results', sourceType: 'lab', detail: 'Microbiology · 3 days ago', snippet: 'Cultures drawn prior to first antibiotic dose' },
        { id: 4, source: 'Vitals / Nursing Flowsheet', sourceType: 'vitals', detail: 'Telemetry · last 24 hours', snippet: 'Afebrile x24h; pain controlled' },
        { id: 5, source: 'Procedure Note — PICC Placement', sourceType: 'note', detail: 'Interventional Radiology · 2 days ago', snippet: 'PICC placed for outpatient IV therapy' },
      ],
      keyFindings: [
        'Excellent response to IV antibiotics - cellulitis improving significantly',
        'Afebrile for 24 hours with stable vital signs',
        'PICC line placed successfully for home IV antibiotic completion',
        'Lives independently, capable of managing home infusion therapy',
        'No underlying chronic conditions or immunosuppression',
        'Expected full recovery with completion of 7-day IV antibiotic course',
      ],
      events: [
        { date: '2026-07-26T00:00:00', type: 'er_visit', location: 'City Medical Center - Emergency Department', description: 'ED visit for severe right leg cellulitis with fever' },
        { date: '2026-07-26T00:00:00', type: 'admission', location: 'City Medical Center - Emergency Department', description: 'Admitted for IV antibiotic therapy for cellulitis' },
        { date: '2026-07-26T00:00:00', type: 'medication_change', location: 'City Medical Center - ED', description: 'Started IV ceftriaxone 2g daily, blood cultures obtained' },
        { date: '2026-07-27T00:00:00', type: 'procedure', location: 'City Medical Center - Interventional Radiology', description: 'PICC line placement for home IV therapy' },
        { date: '2026-07-28T00:00:00', type: 'other', location: 'City Medical Center - Emergency Department', description: 'Afebrile x24 hours, cellulitis significantly improved' },
        { date: '2026-07-29T00:00:00', type: 'other', location: 'City Medical Center', description: 'Home infusion company coordinated - first home visit scheduled' },
      ],
    },
    assignedTo: 'Sarah Johnson',
  },
  {
    id: 'disch-15',
    dispositionScore: 92,
    currentLOS: 3,
    name: 'Elizabeth Wilson',
    age: 82,
    dob: '01/20/1942',
    mrn: 'MRN001242',
    facilityName: 'City Medical Center',
    floor: 5, room: 512,
    disposition: 'hospice',
    urgency: 'urgent',
    phase: 'predict',
    insurance: 'Medicare',
    insuranceType: 'medicare',
    memberId: 'M1A-620-41185',
    admitDate: '4/23/2026',
    losTargetDays: 5,
    attendingPhysician: 'Dr. Christopher Moore',
    primaryDiagnosis: 'End-stage Cancer',
    primaryContact: { name: 'Margaret Wilson', relationship: 'Daughter', phone: '(212) 555-0195' },
    programScores: { hospice: 92, palliative: 76, home_health: 48 },
    decisionFactors: {
      hospice: [
        { id: 'disch-15-hospice-prognosis', label: 'Prognosis', importance: 98, value: 'Life expectancy < 6 months (weeks to months)', dataSource: 'Oncology Assessment', timestamp: '2026-07-27T08:16:00', contribution: 'positive' },
        { id: 'disch-15-hospice-goals', label: 'Goals of Care', importance: 96, value: 'Comfort measures only - DNR/DNI in place', dataSource: 'Family Meeting Notes', timestamp: '2026-07-27T20:16:00', contribution: 'positive' },
        { id: 'disch-15-hospice-caregiver', label: 'Caregiver Support', importance: 88, value: 'Son and daughter-in-law provide 24/7 care', dataSource: 'Social Work Notes', timestamp: '2026-07-27T20:16:00', contribution: 'positive' },
      ],
      palliative: [
        { id: 'disch-15-palliative-symptom', label: 'Symptom Management', importance: 88, value: 'Complex pain management on fentanyl patch', dataSource: 'Oncology Assessment', timestamp: '2026-07-27T08:16:00', contribution: 'positive' },
        { id: 'disch-15-palliative-eligibility', label: 'Hospice Eligibility', importance: 85, value: 'Meets hospice criteria - hospice preferred', dataSource: 'Family Meeting Notes', timestamp: '2026-07-27T20:16:00', contribution: 'negative' },
      ],
      home_health: [
        { id: 'disch-15-hh-skilled', label: 'Skilled Care Appropriateness', importance: 88, value: 'Comfort-focused care, not skilled nursing needs', dataSource: 'Nursing Assessment', timestamp: '2026-07-27T08:16:00', contribution: 'negative' },
        { id: 'disch-15-hh-goals-mismatch', label: 'Goals of Care Mismatch', importance: 82, value: 'End-of-life care focus', dataSource: 'Family Meeting Notes', timestamp: '2026-07-27T20:16:00', contribution: 'negative' },
      ],
    },
    ninetyDaySummary: {
      trend: 'declining',
      trendDescription: 'Progressive decline from metastatic pancreatic cancer with three hospitalizations in 90 days. Functional status deteriorated from ambulatory to bedbound. Pain burden increasing despite escalating medications. Transitioned to hospice-focused care.',
      recentAdmissions: 3,
      erVisits: 0,
      totalInpatientDays: 14,
      narrativeSummary: `Mrs. Wilson is an 82-year-old female with end-stage metastatic pancreatic cancer[[1]] who has elected to transition to hospice care. Over the past 90 days, her condition has progressively declined with increasing symptom burden, multiple hospitalizations for pain and symptom management, and deteriorating functional status.\n\nShe was initially diagnosed with pancreatic cancer 18 months ago. She completed 6 cycles of FOLFIRINOX chemotherapy with initial partial response, but recent imaging 75 days ago showed widespread disease progression with new liver metastases, peritoneal carcinomatosis, and malignant ascites[[2]]. Her oncologist recommended discontinuing chemotherapy given minimal benefit and declining performance status.\n\nOver the past 90 days, she has had three hospital encounters[[3]]: (1) admission 66 days ago for malignant bowel obstruction managed conservatively without surgery (7-day stay), (2) admission 25 days ago for uncontrolled abdominal pain requiring escalation to fentanyl patch with oxycodone breakthrough[[4]] (4-day stay), and (3) current admission 3 days ago for worsening pain, decreased oral intake, and family exhaustion. She has had progressive functional decline from ambulatory with walker to completely bedbound and dependent for all ADLs[[5]].\n\nAfter extensive discussions with her oncologist, the hospitalist team, and her family, she has made the informed decision to focus on comfort measures and quality of life rather than life-prolonging treatments. Prognosis estimated at weeks to months. A family meeting was held to discuss hospice services. She, her son, and daughter-in-law are all in agreement with this transition to hospice care at home. Hospice agency has accepted her and is ready for transition upon discharge.`,
      references: [
        { id: 1, source: 'Oncology Consult Note', sourceType: 'note', detail: 'Dr. P. Nadeem, Oncology · 5 days ago', snippet: 'End-stage metastatic pancreatic adenocarcinoma' },
        { id: 2, source: 'CT Abdomen/Pelvis', sourceType: 'imaging', detail: 'Radiology · 75 days ago', snippet: 'New hepatic metastases, peritoneal carcinomatosis, ascites' },
        { id: 3, source: 'Encounter / Utilization History', sourceType: 'claims', detail: 'Utilization summary · last 90 days', snippet: '3 admissions in 90 days for symptom management' },
        { id: 4, source: 'Medication Reconciliation', sourceType: 'medication', detail: 'Palliative Care · 25 days ago', snippet: 'Escalated to fentanyl patch with oxycodone breakthrough' },
        { id: 5, source: 'Functional Status Assessment', sourceType: 'assessment', detail: 'Nursing · 3 days ago', snippet: 'Bedbound; dependent for all ADLs (ECOG 4)' },
      ],
      keyFindings: [
        'End-stage metastatic pancreatic cancer with widespread disease progression',
        'Prognosis estimated at weeks to months per oncology team',
        'Progressive functional decline - now completely bedbound and dependent for all ADLs',
        'Three hospitalizations in past 90 days for pain management and complications',
        'Pain escalated to fentanyl patch 75mcg with oxycodone 10mg breakthrough',
        'Goals of care transitioned to comfort measures only - hospice elected',
        'Strong family support - son and daughter-in-law provide 24/7 care in home',
        'Hospice agency coordinated and ready for home discharge',
      ],
      events: [
        { date: '2026-07-26T00:00:00', type: 'admission', location: 'City Medical Center - Oncology', description: 'Admitted for worsening pain, decreased PO intake, and family exhaustion' },
        { date: '2026-07-28T00:00:00', type: 'other', location: 'City Medical Center', description: 'Family meeting held - hospice care elected, goals transitioned to comfort only' },
        { date: '2026-07-28T00:00:00', type: 'other', location: 'City Medical Center - Hospice Coordination', description: 'Hospice eligibility confirmed - physician certification completed' },
        { date: '2026-07-08T00:00:00', type: 'discharge', location: 'City Medical Center', description: 'Discharged home with escalated pain regimen after 4-day admission', destination: 'Home' },
        { date: '2026-07-04T00:00:00', type: 'admission', location: 'City Medical Center - Oncology', description: 'Admitted for uncontrolled abdominal pain' },
        { date: '2026-07-04T00:00:00', type: 'medication_change', location: 'City Medical Center - Pain Management', description: 'Pain regimen escalated to fentanyl patch 75mcg + oxycodone 10mg breakthrough' },
        { date: '2026-06-19T00:00:00', type: 'procedure', location: 'City Medical Center - Interventional Radiology', description: 'Palliative paracentesis performed for symptomatic ascites (3L removed)' },
        { date: '2026-05-31T00:00:00', type: 'discharge', location: 'City Medical Center', description: 'Discharged after 7-day admission for bowel obstruction (managed conservatively)', destination: 'Home' },
        { date: '2026-05-24T00:00:00', type: 'admission', location: 'City Medical Center - Oncology', description: 'Admitted for malignant bowel obstruction - conservative management without surgery' },
        { date: '2026-05-15T00:00:00', type: 'other', location: 'Oncology Clinic', description: 'Oncology clinic visit - imaging shows disease progression, chemotherapy discontinued' },
      ],
    },
    assignedTo: 'Sarah Johnson',
  },
];

interface DischargeProviderProps {
  children: ReactNode;
}

// Demo: anchor each patient's admit date to today minus currentLOS so the
// expected discharge column (admit + losTarget) always lands relative to "now".
function formatAdmitDate(daysAgo: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - daysAgo);
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

// Real Seattle-area home addresses so any patient scheduled for real from the
// Intake queue geocodes successfully (reused from the Predict demo patients).
const HOME_ADDRESSES: Record<string, string> = {
  'disch-01': '1234 Pike St, Seattle, WA 98101',
  'disch-02': '789 Madison St, Seattle, WA 98104',
  'disch-03': '321 Union St, Seattle, WA 98101',
  'disch-04': '567 4th Ave N, Seattle, WA 98109',
  'disch-05': '456 Bellevue Way NE, Bellevue, WA 98004',
  'disch-06': '890 East Olive Way, Seattle, WA 98102',
  'disch-07': '234 35th Ave NE, Seattle, WA 98105',
  'disch-08': '123 NW 85th St, Seattle, WA 98117',
  'disch-09': '789 Westlake Ave N, Seattle, WA 98109',
  'disch-10': '456 21st Ave E, Seattle, WA 98112',
  'disch-11': '789 Rainier Ave S, Seattle, WA 98118',
  'disch-12': '234 Queen Anne Ave N, Seattle, WA 98109',
  'disch-13': '567 5th Ave S, Seattle, WA 98104',
  'disch-14': '890 California Ave SW, Seattle, WA 98116',
  'disch-15': '355 Greenwood Ave N, Seattle, WA 98103',
};

export const DischargeProvider: React.FC<DischargeProviderProps> = ({ children }) => {
  const [patients, setPatients] = useState<DischargePatient[]>(() =>
    INITIAL_PATIENTS.map(p => ({
      ...p,
      admitDate: formatAdmitDate(p.currentLOS),
      recommendedDisposition: p.disposition,
      homeAddress: HOME_ADDRESSES[p.id] ?? p.homeAddress,
    })),
  );

  const takeOwnership = useCallback((patientId: string) => {
    setPatients(prev => prev.map(p => p.id === patientId ? { ...p, assignedTo: DEMO_CURRENT_USER } : p));
  }, []);

  const setPhase = useCallback((patientId: string, phase: DischargePhase) => {
    setPatients(prev => prev.map(p => p.id === patientId ? { ...p, phase } : p));
  }, []);

  const setSelectedProvider = useCallback((patientId: string, providerName: string | undefined) => {
    setPatients(prev => prev.map(p => p.id === patientId ? { ...p, selectedProviderName: providerName } : p));
  }, []);

  const setDisposition = useCallback((patientId: string, disposition: DischargeDisposition) => {
    setPatients(prev => prev.map(p => p.id === patientId ? { ...p, disposition } : p));
  }, []);

  const setReferralSent = useCallback((patientId: string, sentAt: string | undefined) => {
    setPatients(prev => prev.map(p => p.id === patientId ? { ...p, referralSentAt: sentAt } : p));
  }, []);

  const setReferralResponse = useCallback((patientId: string, response: 'awaiting' | 'no_response' | 'declined' | undefined) => {
    setPatients(prev => prev.map(p => p.id === patientId ? { ...p, referralResponse: response } : p));
  }, []);

  const setDeclinedProvider = useCallback((patientId: string, providerName: string | undefined) => {
    setPatients(prev => prev.map(p => p.id === patientId ? { ...p, declinedProviderName: providerName } : p));
  }, []);

  const setNoResponseProvider = useCallback((patientId: string, providerName: string | undefined) => {
    setPatients(prev => prev.map(p => p.id === patientId ? { ...p, noResponseProviderName: providerName } : p));
  }, []);

  const toggleMonitorCheck = useCallback((patientId: string, key: string) => {
    setPatients(prev => prev.map(p => {
      if (p.id !== patientId) return p;
      const current = p.monitorChecks ?? [];
      return {
        ...p,
        monitorChecks: current.includes(key) ? current.filter(k => k !== key) : [...current, key],
      };
    }));
  }, []);

  const sendToFollowUp = useCallback((patientId: string) => {
    setPatients(prev => prev.map(p => p.id === patientId
      ? {
          ...p,
          phase: 'followup',
          agencyUpdate: { ...buildAgencyFollowUp(p, 0), updatedAt: new Date().toISOString(), revision: 0 },
        }
      : p));
  }, []);

  const requestAgencyUpdate = useCallback((patientId: string) => {
    setPatients(prev => prev.map(p => {
      if (p.id !== patientId || !p.agencyUpdate) return p;
      const revision = p.agencyUpdate.revision + 1;
      return {
        ...p,
        agencyUpdate: { ...buildAgencyFollowUp(p, revision), updatedAt: new Date().toISOString(), revision },
      };
    }));
  }, []);

  const sendToIntake = useCallback((patientId: string) => {
    setPatients(prev => prev.map(p => p.id === patientId
      ? { ...p, sentToIntakeAt: new Date().toISOString(), scheduledAt: undefined }
      : p));
  }, []);

  const markScheduled = useCallback((patientId: string) => {
    setPatients(prev => prev.map(p => p.id === patientId
      ? { ...p, scheduledAt: new Date().toISOString() }
      : p));
  }, []);

  const value = useMemo<DischargeContextType>(() => ({
    patients,
    getPatientById: (id: string) => patients.find(p => p.id === id),
    currentUser: DEMO_CURRENT_USER,
    takeOwnership,
    setPhase,
    setDisposition,
    setSelectedProvider,
    setReferralSent,
    setReferralResponse,
    setDeclinedProvider,
    setNoResponseProvider,
    toggleMonitorCheck,
    sendToFollowUp,
    requestAgencyUpdate,
    sendToIntake,
    markScheduled,
  }), [patients, takeOwnership, setPhase, setDisposition, setSelectedProvider, setReferralSent, setReferralResponse, setDeclinedProvider, setNoResponseProvider, toggleMonitorCheck, sendToFollowUp, requestAgencyUpdate, sendToIntake, markScheduled]);

  return <DischargeContext.Provider value={value}>{children}</DischargeContext.Provider>;
};
